import React, { useMemo } from 'react';

// Mapping of airport codes to IANA timezone names
const airportTimezones = {
  'HND': 'Asia/Tokyo',
  'NRT': 'Asia/Tokyo',
  'KIX': 'Asia/Tokyo',
  'NGO': 'Asia/Tokyo',
  'HNL': 'Pacific/Honolulu',
  'JFK': 'America/New_York',
  'BOS': 'America/New_York',
  'ORD': 'America/Chicago',
  'DFW': 'America/Dallas',
  'LAX': 'America/Los_Angeles',
  'SAN': 'America/Los_Angeles',
  'SFO': 'America/Los_Angeles',
  'SEA': 'America/Los_Angeles',
  'YVR': 'America/Los_Angeles',
};

const FlightCalendar = ({ flights, dateRange, loading }) => {
  // Determine the origin timezone (assume all flights have the same origin)
  const originTimezone = flights.length > 0 ? airportTimezones[flights[0].origin] || 'UTC' : 'UTC';

  // Group flights by local date in origin timezone
  const calendarData = useMemo(() => {
    if (!flights.length) return {};

    const data = {};
    flights.forEach(flight => {
      if (!flight.depart_time || !flight.origin) return;

      const departTime = new Date(flight.depart_time);
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: originTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const [{ value: month }, , { value: day }, , { value: year }] = formatter.formatToParts(departTime);
      const date = `${year}-${month}-${day}`;

      if (!data[date]) {
        data[date] = { minPoints: flight.points, count: 1 };
      } else {
        data[date].minPoints = Math.min(data[date].minPoints, flight.points);
        data[date].count += 1;
      }
    });
    return data;
  }, [flights, originTimezone]);

  // Generate months with proper alignment
  const months = useMemo(() => {
    if (!dateRange.startDate || !dateRange.endDate) return [];

    // Helper function to create a date in the origin timezone
    const createDateInTimezone = (year, month, day) => {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: originTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}T00:00:00`;
      const date = new Date(dateStr);
      const [{ value: adjustedMonth }, , { value: adjustedDay }, , { value: adjustedYear }] = formatter.formatToParts(date);
      return new Date(adjustedYear, parseInt(adjustedMonth) - 1, adjustedDay);
    };

    const start = new Date(dateRange.startDate);
    const end = new Date(dateRange.endDate);
    const months = [];

    const getLocalDateStr = (date) => {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: originTimezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      });
      const [{ value: month }, , { value: day }, , { value: year }] = formatter.formatToParts(date);
      return `${year}-${month}-${day}`;
    };

    const dayIndexMap = {
      'Sunday': 0,
      'Monday': 1,
      'Tuesday': 2,
      'Wednesday': 3,
      'Thursday': 4,
      'Friday': 5,
      'Saturday': 6,
    };

    let currentMonth = new Date(start.getFullYear(), start.getMonth(), 1);
    while (currentMonth <= end) {
      const monthStart = createDateInTimezone(currentMonth.getFullYear(), currentMonth.getMonth(), 1);

      // Generate days for this month
      const daysInMonth = new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 0).getDate();
      const days = [];
      for (let day = 1; day <= daysInMonth; day++) {
        const date = createDateInTimezone(currentMonth.getFullYear(), currentMonth.getMonth(), day);
        const dateStr = getLocalDateStr(date);
        if (date < start || date > end) continue;

        const dayData = calendarData[dateStr] || { minPoints: null, count: 0 };
        days.push({
          date,
          dateStr,
          minPoints: dayData.minPoints,
          count: dayData.count,
          isToday: dateStr === getLocalDateStr(new Date()),
        });
      }

      // Calculate empty cells based on the first day rendered
      let emptyCellsBefore = 0;
      if (days.length > 0) {
        const firstDayDate = days[0].date;
        const firstDayName = new Intl.DateTimeFormat('en-US', {
          timeZone: originTimezone,
          weekday: 'long',
        }).format(firstDayDate);
        emptyCellsBefore = dayIndexMap[firstDayName];
      }

      months.push({
        year: currentMonth.getFullYear(),
        month: currentMonth.getMonth(),
        name: new Intl.DateTimeFormat('en-US', {
          timeZone: originTimezone,
          month: 'long',
        }).format(currentMonth),
        yearMonth: getLocalDateStr(monthStart).slice(0, 7),
        emptyCellsBefore,
        days,
      });

      currentMonth.setMonth(currentMonth.getMonth() + 1);
    }

    return months;
  }, [dateRange, calendarData, originTimezone]);

  // Get color for points value
  const getPointsColor = (points) => {
    if (!points) return 'bg-gray-100 text-gray-400';
    if (points <= 10000) return 'bg-green-100 text-green-800';
    if (points <= 20000) return 'bg-blue-100 text-blue-800';
    if (points <= 30000) return 'bg-yellow-100 text-yellow-800';
    if (points <= 50000) return 'bg-orange-100 text-orange-800';
    return 'bg-red-100 text-red-800';
  };

  const daysOfWeek = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  if (loading) {
    return (
      <div className="h-64 flex items-center justify-center">
        <div className="text-gray-500">Loading calendar data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {months.map((month) => (
        <div key={month.yearMonth} className="space-y-2">
          <h3 className="text-lg font-medium text-gray-900">
            {month.name} {month.year}
          </h3>
          <div className="grid grid-cols-7 gap-1">
            {/* Day headers */}
            {daysOfWeek.map((day) => (
              <div
                key={day}
                className="text-xs font-medium text-gray-500 text-center p-2"
              >
                {day}
              </div>
            ))}
            {/* Empty cells for alignment */}
            {Array.from({ length: month.emptyCellsBefore }).map((_, i) => (
              <div key={`empty-start-${i}`} className="h-16 bg-gray-50 rounded-md" />
            ))}
            {/* Calendar cells */}
            {month.days.map((day) => {
              const colorClass = getPointsColor(day.minPoints);
              return (
                <div
                  key={day.dateStr}
                  className={`h-16 rounded-md border ${
                    day.isToday ? 'border-blue-500' : 'border-gray-200'
                  } overflow-hidden`}
                >
                  <div className="flex flex-col h-full">
                    <div className="text-xs font-medium p-1 text-right text-gray-700">
                      {new Intl.DateTimeFormat('en-US', {
                        timeZone: originTimezone,
                        day: 'numeric',
                      }).format(day.date)}
                    </div>
                    {day.minPoints ? (
                      <div
                        className={`flex-1 flex items-center justify-center ${colorClass} text-xs p-1`}
                      >
                        <div className="text-center">
                          <div className="font-semibold">
                            {day.minPoints.toLocaleString()}
                          </div>
                          <div>
                            {day.count} {day.count === 1 ? 'flight' : 'flights'}
                          </div>
                        </div>
                      </div>
                    ) : (
                      <div className="flex-1 flex items-center justify-center bg-gray-50 text-xs text-gray-400">
                        No data
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}
      {/* Legend */}
      <div className="flex flex-wrap gap-2 mt-4">
        <div className="flex items-center">
          <span className="w-4 h-4 inline-block bg-green-100 rounded mr-1"></span>
          <span className="text-xs text-gray-600">= 10K points</span>
        </div>
        <div className="flex items-center">
          <span className="w-4 h-4 inline-block bg-blue-100 rounded mr-1"></span>
          <span className="text-xs text-gray-600">= 20K points</span>
        </div>
        <div className="flex items-center">
          <span className="w-4 h-4 inline-block bg-yellow-100 rounded mr-1"></span>
          <span className="text-xs text-gray-600">= 30K points</span>
        </div>
        <div className="flex items-center">
          <span className="w-4 h-4 inline-block bg-orange-100 rounded mr-1"></span>
          <span className="text-xs text-gray-600">= 50K points</span>
        </div>
        <div className="flex items-center">
          <span className="w-4 h-4 inline-block bg-red-100 rounded mr-1"></span>
          <span className="text-xs text-gray-600">> 50K points</span>
        </div>
      </div>
    </div>
  );
};

export default FlightCalendar;